// Text on the map: measured with a 2D canvas exactly as the deck's original 2D-canvas drawing measured it, so badges and bubbles keep
// their sizes and line breaks, and drawn as Phaser Text placed so its baseline lands where fillText put it.
import { uiFont } from '/i18n.js';
const ctx = document.createElement('canvas').getContext('2d');
// The map's fonts are written 'bold 12px system-ui'; drawn and measured with the UI font instead (index.html --ui-font: on
// a page not in Chinese it names Simplified Chinese fonts, so Chinese names and chat don't draw with Japanese glyphs).
const ui = (font) => font.replace(/system-ui$/, uiFont());
const widths = new Map(); // font + text -> width: names and labels are measured every frame
export function measure(font, text) {
  font = ui(font);
  const k = font + '\n' + text; let w = widths.get(k);
  if (w === undefined) { if (widths.size > 4000) widths.clear(); ctx.font = font; w = ctx.measureText(text).width; widths.set(k, w); }
  return w;
}

const metricsCache = new Map();
/** Font metrics for Phaser Text (so it skips its own guess): ascent/descent of the font box, and where a 'top'
 *  baseline sits above the alphabetic one. */
export function fontMetrics(font) {
  font = ui(font);
  let m = metricsCache.get(font);
  if (m) return m;
  ctx.font = font; ctx.textBaseline = 'alphabetic';
  const t = ctx.measureText('Mg\u4e2d\u{1F600}') // tall Latin, a Han glyph (U+4E2D) and an emoji: the font box of them all;
  const ascent = Math.ceil(t.fontBoundingBoxAscent ?? t.actualBoundingBoxAscent), descent = Math.ceil(t.fontBoundingBoxDescent ?? t.actualBoundingBoxDescent);
  ctx.textBaseline = 'top';
  const top = ctx.measureText('M').alphabeticBaseline; // 'top' baseline -> alphabetic baseline (negative: it is below)
  ctx.textBaseline = 'alphabetic';
  m = { ascent, descent, fontSize: ascent + descent, topToBaseline: Number.isFinite(top) ? -top : ascent };
  metricsCache.set(font, m);
  return m;
}
/** Phaser Text style for a CSS font shorthand like 'bold 13px system-ui'. */
export function textStyle(font, color) {
  font = ui(font);
  const m = /^(bold\s+)?(\d+(?:\.\d+)?)px\s+(.+)$/.exec(font);
  const { ascent, descent, fontSize } = fontMetrics(font);
  return { fontFamily: m[3], fontSize: m[2] + 'px', fontStyle: m[1] ? 'bold' : '', color, padding: { x: 0, y: 0 }, metrics: { ascent, descent, fontSize } };
}
/** Put a Text so that its alphabetic baseline is at screen y and its left edge at screen x. */
export function placeText(t, x, baselineY, font) { t.setPosition(Math.round(x), Math.round(baselineY) - fontMetrics(font).ascent); }

// ---- chat bubbles' line breaking (unchanged from the original 2D-canvas drawing)
export const BUBBLE_FONT = '13px system-ui', BUBBLE_W = 190, BUBBLE_LINES = 3;
export function bubbleLines(text) {
  ctx.font = ui(BUBBLE_FONT);
  const lines = []; let cur = '';
  for (const ch of text) {
    // fits, or is punctuation that must not start a line (it may poke out a little instead)
    if (ctx.measureText(cur + ch).width <= BUBBLE_W || !cur || /[，。、！？；：）》」』”’…,.!?;:)\]~～]/.test(ch)) { cur += ch; continue; }
    // break Latin words at the last space if there is one; CJK can break anywhere
    const sp = /\S/.test(ch) && /[\x21-\x7e]$/.test(cur) ? cur.lastIndexOf(' ') : -1;
    if (sp > 0) { lines.push(cur.slice(0, sp)); cur = cur.slice(sp + 1) + ch; } else { lines.push(cur); cur = ch.trim(); }
    if (lines.length === BUBBLE_LINES) break;
  }
  if (lines.length < BUBBLE_LINES) { if (cur) lines.push(cur); return lines; }
  // more than fits: the last line ends with an ellipsis
  let last = lines[BUBBLE_LINES - 1];
  while (last && ctx.measureText(last + '…').width > BUBBLE_W) last = [...last].slice(0, -1).join('');
  lines[BUBBLE_LINES - 1] = last + '…';
  return lines;
}
